import React, { useCallback, useRef, useState } from 'react';
import { searchAddressGateway } from '@/di-container/common/search-address/gateway/client';
import { GetSearchAddressRequest, SearchAddress } from '@remodeling/common';
import { Address } from '@remodeling/remodeling-manager';
import { useWebApp } from '@/providers/WebAppProvider';

interface UseSearchZoneCodeProps {
  handleChangeSiteData: (data: Address) => void;
}

const useSearchZoneCode = ({
  handleChangeSiteData,
}: UseSearchZoneCodeProps) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [isSearchable, setIsSearchable] = useState<boolean>(false);
  const [inputError, setInputError] = useState<string>('');
  const [list, setList] = useState<SearchAddress[]>();
  const [totalNum, setTotalNum] = useState<number>(0);
  const [isLast, setIsLast] = useState<boolean>();
  const [page, setPage] = useState<number>(1);
  const inputRef = useRef<HTMLInputElement>(null);
  const { getSearchAddress } = searchAddressGateway();
  const { isAOS } = useWebApp();

  const handleChangeInput = (e: React.FormEvent<HTMLInputElement>) => {
    setInputValue(e.currentTarget.value);
  };

  const handleClickDelete = () => {
    setInputValue('');
  };

  const getSearchAddressList = useCallback(async () => {
    const reqData: GetSearchAddressRequest = {
      query: inputValue,
      page: 1,
      size: 15,
    };
    const response = await getSearchAddress.exec(reqData);
    if (response?.data) {
      setList(response.data.content);
      setTotalNum(response.data.totalElements);
      setIsLast(response.data.last);
    } else {
      setList(undefined);
      setTotalNum(0);
      setIsLast(true);
    }
  }, [inputValue, getSearchAddress]);

  const handleReadMore = useCallback(async () => {
    if (isLast) {
      setPage(1);
      return;
    }
    const _page = page + 1;
    const reqData: GetSearchAddressRequest = {
      query: inputValue,
      page: _page,
      size: 15,
    };
    const response = await getSearchAddress.exec(reqData);
    if (response?.data) {
      setList(list?.concat(response.data.content));
      setPage(_page);
      setIsLast(response.data.last);
    }
  }, [list, isLast, inputValue, page, getSearchAddress]);

  const validatingSearchInput = useCallback(() => {
    if (inputValue.length === 0) {
      return '검색어를 입력해 주세요.';
    } else if (inputValue.length === 1) {
      return '검색어는 최소 2글자 이상 입력해 주세요.';
    } else if (inputValue.length > 30) {
      return '검색어가 너무 깁니다.';
    }
    return '';
  }, [inputValue]);

  const handleEmptySearch = () => {
    setList(undefined);
    setTotalNum(0);
    setIsLast(true);
    setIsSearchable(false);
  };

  const handleSearchSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();
      isAOS() && (await inputRef.current?.blur());
      setPage(1);

      const errorMessage = validatingSearchInput();

      errorMessage || (await handleEmptySearch());
      errorMessage || (await getSearchAddressList());
      setInputError(errorMessage);
      setIsSearchable(!errorMessage);
    },
    [getSearchAddressList, validatingSearchInput, isAOS],
  );

  const handleClickAddressItem = (item: SearchAddress) => {
    handleChangeSiteData({
      sidoCode: item.lglSidoCd,
      houseCode: item.houseCd,
      sidoName: item.sidoNm,
      sigunguCode: item.lglSiguCd,
      sigunguName: item.siguNm,
      zoneCode: item.post5No,
      dongCode: item.lglDngCd,
      dongName: item.dngNm,
      roadnameCode: item.roadCd,
      roadnameAddress: item.addrRoad,
      buildingCode: item.bldgMgmtNo,
      buildingName: item.bldgNm,
      x: parseFloat(item.wgs84XBldgAvg),
      y: parseFloat(item.wgs84YBldgAvg),
    });
    handleEmptySearch();
    setInputValue('');
  };

  return {
    handleChangeInput,
    inputValue,
    handleClickDelete,
    isSearchable,
    handleSearchSubmit,
    inputError,
    list,
    totalNum,
    handleReadMore,
    handleClickAddressItem,
    handleEmptySearch,
    setInputValue,
    inputRef,
  };
};

export default useSearchZoneCode;
